import { useMemo } from "react";

import useURLParams from "hooks/useURLParams";
import { URL_SEARCH_KEY } from "constants/url_query_keys";
import { fuzzySearch } from "utils/fuzzySearch";
import SearchInput from "components/SearchInput";
import SortableTable from "components/SortableTable";
import ListEntitiesItem from "components/ListEntitiesItem";
import ListEntitiesItemTitle from "components/ListEntitiesItem/Title";
import Typography from "ds/components/Typography";
import { SearchQueryOrderDirection } from "types/generated";
import EmptyState from "ds/components/EmptyState";
import { EmptystateSearchNoResultsColored } from "components/icons/generated";
import Box from "ds/components/Box";
import PageInfoHeaderWrapper from "ds/components/PageInfoHeader/Wrapper";
import PageInfoHeaderActions from "ds/components/PageInfoHeader/Actions";

import { columns, FILTERS_ORDER_SETTINGS_KEY_VERSION_OUTPUTS } from "./constants";
import ModuleVersionScrollableWrapper from "../components/ScrollableWrapper";
import ModuleVersionPageIntoTitle from "../components/PageIntoTitle";

type ModuleVersionOutputsProps = {
  outputs: Array<{
    name: string;
    description?: string;
  }>;
};

const ModuleVersionOutputs = ({ outputs }: ModuleVersionOutputsProps) => {
  const urlParams = useURLParams();
  const searchQuery = urlParams.get(URL_SEARCH_KEY);

  const filteredOutputs = useMemo(() => {
    const results = outputs.map((output) => ({
      ...output,
      id: output.name,
    }));

    if (searchQuery) {
      return fuzzySearch(results, searchQuery.trim(), {
        keys: ["description", "name"],
        scoreThreshold: -1000,
      });
    }

    return results;
  }, [outputs, searchQuery]);

  return (
    <>
      <PageInfoHeaderWrapper>
        <ModuleVersionPageIntoTitle
          title="Outputs"
          tooltip="Outputs declared by the module. Can be used as inputs to other modules."
        />
        <PageInfoHeaderActions>
          <SearchInput
            placeholder="Search by name and description..."
            filtersOrderSettingsKey={FILTERS_ORDER_SETTINGS_KEY_VERSION_OUTPUTS}
          />
        </PageInfoHeaderActions>
      </PageInfoHeaderWrapper>
      <ModuleVersionScrollableWrapper padding="0">
        {filteredOutputs.length === 0 && (
          <EmptyState
            padding="x-large"
            icon={EmptystateSearchNoResultsColored}
            caption={
              searchQuery
                ? "No outputs match your search query"
                : "This module has no output variables"
            }
            fullHeight
            announce={!!searchQuery}
          />
        )}
        {filteredOutputs.length > 0 && (
          <SortableTable
            ariaLabel="Outputs"
            items={filteredOutputs}
            columns={columns}
            columnOrder="1fr"
            stickyHeader
            renderItems={(sorted) =>
              sorted.map((item) => (
                <ListEntitiesItem role="row" key={item.id}>
                  <Box role="rowheader" direction="column" gap="small">
                    <ListEntitiesItemTitle title={item.id} titleVariant="p-body2" />
                    {item.description && (
                      <Typography role="cell" tag="p" variant="p-body3" color="secondary">
                        {item.description}
                      </Typography>
                    )}
                  </Box>
                </ListEntitiesItem>
              ))
            }
            initialSortBy="id"
            initialDirection={SearchQueryOrderDirection.Desc}
          />
        )}
      </ModuleVersionScrollableWrapper>
    </>
  );
};

export default ModuleVersionOutputs;
